import {
    getResponseData,
    mergeMessageLists,
    normalizeMessages
} from '@/utils/chat/messageUtils';

import axios from 'axios';
import { bus } from '@/main';
import toast from '@/services/toast';

const LIVE_CACHE_DELAY = 250;

export default {
    data() {
        return {
            selectedRoomInfo: null,
            selectedActvRoomId: '',

            messages: [],
            messageListResultsDto: null,
            msgNextPageLoader: null,
            msgApiLoader: false,
            isFetchingMessages: false,
            isRefreshingMessages: false,
            currentChatRequestId: 0,
            chatRequestCancel: null,
            paginationRequestCancel: null,

            messageCacheSaveTimer: null
        };
    },

    computed: {
        reversedMessages() {
            return normalizeMessages(
                this.messages
            ).slice().reverse();
        }
    },

    mounted() {
        bus.$on(
            'messageDeletedBus',
            this.handleMessageDeletedForEveryone
        );

        bus.$on(
            'messageRemovedForSelfBus',
            this.handleMessageRemovedForSelf
        );
    },

    beforeDestroy() {
        bus.$off(
            'messageDeletedBus',
            this.handleMessageDeletedForEveryone
        );

        bus.$off(
            'messageRemovedForSelfBus',
            this.handleMessageRemovedForSelf
        );

        this.clearScheduledRoomCacheSave();
    },

    methods: {
        handleMessageDeletedForEveryone(payload) {
            if (!this.isValidDeletePayload(payload)) {
                return;
            }

            if (!this.isPayloadForActiveRoom(payload)) {
                return;
            }

            const messageIndex =
                this.findMessageIndexById(payload.id);

            if (messageIndex === -1) {
                return;
            }

            const currentMessage =
                this.messages[messageIndex];

            const updatedMessage = Object.assign(
                {},
                currentMessage,
                {
                    is_deleted: true,
                    deleted_for_everyone: true,
                    message: 'This message was deleted',
                    content: 'This message was deleted',

                    // Clear possible message-content fields.
                    file: null,
                    files: [],
                    attachment: null,
                    attachments: [],
                    image: null,
                    document: null,
                    message_file: null,
                    reply_message: null,
                    reply_to: null
                }
            );

            this.$set(
                this.messages,
                messageIndex,
                updatedMessage
            );

            this.syncMessageResults();
            this.scheduleActiveRoomCacheSave();
        },

        handleMessageRemovedForSelf(payload) {
            if (!this.isValidDeletePayload(payload)) {
                return;
            }

            if (!this.isPayloadForActiveRoom(payload)) {
                return;
            }

            const messageIndex =
                this.findMessageIndexById(payload.id);

            if (messageIndex === -1) {
                return;
            }

            this.messages.splice(messageIndex, 1);

            this.syncMessageResults();
            this.scheduleActiveRoomCacheSave();
        },

        findMessageIndexById(messageId) {
            if (!Array.isArray(this.messages)) {
                return -1;
            }

            return this.messages.findIndex((message) => {
                if (
                    !message ||
                    message.id === undefined ||
                    message.id === null
                ) {
                    return false;
                }

                return (
                    String(message.id) ===
                    String(messageId)
                );
            });
        },

        isValidDeletePayload(payload) {
            return Boolean(
                payload &&
                payload.id !== undefined &&
                payload.id !== null &&
                String(payload.id).trim() !== ''
            );
        },

        isPayloadForActiveRoom(payload) {
            if (
                !payload ||
                payload.roomId === undefined ||
                payload.roomId === null ||
                String(payload.roomId).trim() === ''
            ) {
                /*
                 * Support old payloads that contain only the
                 * message ID.
                 */
                return true;
            }

            return (
                String(payload.roomId) ===
                String(this.selectedActvRoomId)
            );
        },

        syncMessageResults() {
            if (!this.messageListResultsDto) {
                this.messageListResultsDto = {
                    results: normalizeMessages(
                        this.messages
                    )
                };

                return;
            }

            this.messageListResultsDto = Object.assign(
                {},
                this.messageListResultsDto,
                {
                    results: normalizeMessages(
                        this.messages
                    )
                }
            );
        },
        handleScroll(event) {
            const element =
                event && event.target
                    ? event.target
                    : event;

            if (element && element.scrollTop <= 1) {
                this.loadMoreMessages(
                    element.scrollHeight,
                    element
                );
            }
        },

        loadMoreMessages(
            previousScrollHeight,
            messagesElement
        ) {
            if (
                !this.msgNextPageLoader ||
                this.isFetchingMessages
            ) {
                return Promise.resolve();
            }

            const nextPageUrl =
                this.msgNextPageLoader;

            const roomId =
                this.selectedActvRoomId;

            const requestId =
                this.currentChatRequestId;

            const source =
                axios.CancelToken.source();

            this.paginationRequestCancel = source;
            this.isFetchingMessages = true;
            this.msgApiLoader = true;

            return axios.get(nextPageUrl, {
                cancelToken: source.token
            })
                .then((response) => {
                    if (
                        requestId !==
                        this.currentChatRequestId ||
                        String(roomId) !==
                        String(
                            this.selectedActvRoomId
                        )
                    ) {
                        return;
                    }

                    const data =
                        getResponseData(response);

                    const olderMessages =
                        normalizeMessages(data.results);

                    this.messages =
                        mergeMessageLists(
                            this.messages,
                            olderMessages
                        );

                    this.msgNextPageLoader =
                        data.next || null;

                    this.saveActiveRoomToCache();

                    this.$nextTick(() => {
                        if (!messagesElement) {
                            return;
                        }

                        messagesElement.scrollTop =
                            messagesElement
                                .scrollHeight -
                            previousScrollHeight;
                    });
                })
                .catch((error) => {
                    if (!axios.isCancel(error)) {
                        console.error(
                            'Unable to load older chat messages:',
                            error
                        );
                    }
                })
                .then(() => {
                    if (
                        requestId ===
                        this.currentChatRequestId
                    ) {
                        this.msgApiLoader = false;
                        this.isFetchingMessages = false;
                    }

                    if (
                        this.paginationRequestCancel ===
                        source
                    ) {
                        this.paginationRequestCancel =
                            null;
                    }
                });
        },

        getResponseData(response) {
            return getResponseData(response);
        },

        normalizeMessages(messages) {
            return normalizeMessages(messages);
        },

        mergeMessageLists(
            primaryMessages,
            secondaryMessages
        ) {
            return mergeMessageLists(
                primaryMessages,
                secondaryMessages
            );
        },

        scheduleActiveRoomCacheSave() {
            this.clearScheduledRoomCacheSave();

            const roomId =
                this.selectedActvRoomId;

            if (!roomId) {
                return;
            }

            this.messageCacheSaveTimer =
                setTimeout(() => {
                    this.messageCacheSaveTimer =
                        null;

                    if (
                        String(roomId) ===
                        String(
                            this.selectedActvRoomId
                        )
                    ) {
                        this.saveActiveRoomToCache();
                    }
                }, LIVE_CACHE_DELAY);
        },

        clearScheduledRoomCacheSave() {
            if (this.messageCacheSaveTimer) {
                clearTimeout(
                    this.messageCacheSaveTimer
                );

                this.messageCacheSaveTimer = null;
            }
        },

        saveActiveRoomToCache() {
            this.clearScheduledRoomCacheSave();

            if (!this.selectedActvRoomId) {
                return;
            }

            const hasMessages =
                Array.isArray(this.messages) &&
                this.messages.length > 0;

            if (
                this.messageListResultsDto === null &&
                !hasMessages
            ) {
                return;
            }

            this.$store.commit(
                'chatMessageCache/SET_ROOM_CACHE',
                {
                    roomId: String(
                        this.selectedActvRoomId
                    ),
                    messages: this.messages,
                    messageListResultsDto:
                        this.messageListResultsDto,
                    msgNextPageLoader:
                        this.msgNextPageLoader ||
                        null
                }
            );
        },

        restoreRoomFromCache(roomId) {
            const roomCacheGetter =
                this.$store.getters[
                'chatMessageCache/roomCacheById'
                ];

            if (
                typeof roomCacheGetter !== 'function'
            ) {
                return false;
            }

            const cachedRoom =
                roomCacheGetter(roomId);

            if (!cachedRoom) {
                return false;
            }

            this.messages = cachedRoom.messages;
            this.messageListResultsDto =
                cachedRoom.messageListResultsDto;

            if (!this.messageListResultsDto) {
                this.messageListResultsDto = {
                    results: []
                };
            }

            this.msgNextPageLoader =
                cachedRoom.msgNextPageLoader ||
                null;

            this.$store.commit(
                'chatMessageCache/TOUCH_ROOM_CACHE',
                roomId
            );

            return true;
        },

        removeRoomFromMessageCache(roomId) {
            if (
                roomId === undefined ||
                roomId === null
            ) {
                return;
            }

            this.$store.commit(
                'chatMessageCache/REMOVE_ROOM_CACHE',
                roomId
            );
        },

        clearMessageCache() {
            this.$store.commit(
                'chatMessageCache/CLEAR_CACHE'
            );
        },

        buildChatRoomMethod(chatRoomInfo) {
            if (
                !chatRoomInfo ||
                chatRoomInfo.chat_room ===
                undefined ||
                chatRoomInfo.chat_room === null
            ) {
                return;
            }

            const roomId = String(
                chatRoomInfo.chat_room
            );

            const socketId =
                roomId.replace(/-/g, '');

            const isSameRoom =
                String(this.selectedActvRoomId) ===
                roomId;

            this.selectedRoomInfo = chatRoomInfo;

            if (
                isSameRoom &&
                (
                    this.messageListResultsDto !==
                    null ||
                    this.isFetchingMessages
                )
            ) {
                if (
                    !this.isChatSocketOpen() &&
                    this.componentActive
                ) {
                    this.initWebSocket(socketId);
                }

                return;
            }

            this.saveActiveRoomToCache();
            this.cancelPendingChatRequests();

            this.stopTypingActivity(
                this.selectedActvRoomId
            );

            this.closeExistingSocket();
            this.resetChatUIState();

            this.selectedActvRoomId = roomId;

            const restoredFromCache =
                this.restoreRoomFromCache(roomId);

            if (!restoredFromCache) {
                this.messages = [];
                this.messageListResultsDto = null;
                this.msgNextPageLoader = null;
            }

            this.isFetchingMessages = true;

            const requestId =
                ++this.currentChatRequestId;

            const source =
                axios.CancelToken.source();

            this.chatRequestCancel = source;
            this.initWebSocket(socketId);

            if (restoredFromCache) {
                this.$nextTick(
                    this.chatScrollBottomMethod
                );
            }

            return this.$store.dispatch(
                'fetchChatDetailsById',
                {
                    id: chatRoomInfo.chat_room,
                    cancelToken: source.token
                }
            )
                .then((response) => {
                    if (
                        requestId !==
                        this.currentChatRequestId ||
                        roomId !==
                        String(
                            this.selectedActvRoomId
                        )
                    ) {
                        return;
                    }

                    const data =
                        getResponseData(response);

                    const apiMessages =
                        normalizeMessages(data.results);

                    // Server data wins over stale cache data.
                    this.messages =
                        mergeMessageLists(
                            apiMessages,
                            this.messages
                        );

                    this.messageListResultsDto =
                        Object.assign({}, data, {
                            results: apiMessages
                        });

                    this.msgNextPageLoader =
                        data.next || null;

                    this.saveActiveRoomToCache();

                    bus.$emit(
                        'unreadCountClearedForUserBus',
                        true
                    );

                    this.$nextTick(
                        this.chatScrollBottomMethod
                    );
                })
                .catch((error) => {
                    if (axios.isCancel(error)) {
                        return;
                    }

                    if (
                        requestId ===
                        this.currentChatRequestId &&
                        !restoredFromCache
                    ) {
                        this.messageListResultsDto = {
                            results: []
                        };
                    }

                    toast.error(
                        'Unable to refresh chat messages. Please try again.'
                    );

                    console.error(
                        'Unable to open chat room:',
                        error
                    );
                })
                .then(() => {
                    if (
                        requestId ===
                        this.currentChatRequestId
                    ) {
                        this.isFetchingMessages = false;
                    }

                    if (
                        this.chatRequestCancel ===
                        source
                    ) {
                        this.chatRequestCancel = null;
                    }
                });
        },

        clearCurrentRoom() {
            this.saveActiveRoomToCache();
            this.cancelPendingChatRequests();

            this.stopTypingActivity(
                this.selectedActvRoomId
            );

            this.closeExistingSocket();
            this.resetChatUIState();
            this.currentChatRequestId += 1;
            this.selectedRoomInfo = null;
            this.selectedActvRoomId = '';
            this.messages = [];
            this.messageListResultsDto = null;
            this.msgNextPageLoader = null;
            this.isFetchingMessages = false;
            this.msgApiLoader = false;
        },

        cancelPendingChatRequests() {
            if (this.chatRequestCancel) {
                this.chatRequestCancel.cancel(
                    'Chat room changed.'
                );

                this.chatRequestCancel = null;
            }

            if (this.paginationRequestCancel) {
                this.paginationRequestCancel.cancel(
                    'Chat room changed.'
                );

                this.paginationRequestCancel =
                    null;
            }
        },

        allMessageListMethod() {
            if (
                !this.selectedRoomInfo ||
                this.isRefreshingMessages
            ) {
                return Promise.resolve();
            }

            const roomId = String(
                this.selectedRoomInfo.chat_room
            );

            const requestId =
                this.currentChatRequestId;

            this.isRefreshingMessages = true;

            return this.$store.dispatch(
                'fetchChatDetailsById',
                {
                    id:
                        this.selectedRoomInfo
                            .chat_room
                }
            )
                .then((response) => {
                    if (
                        requestId !==
                        this.currentChatRequestId ||
                        roomId !==
                        String(
                            this.selectedActvRoomId
                        )
                    ) {
                        return;
                    }

                    const data =
                        getResponseData(response);

                    const apiMessages =
                        normalizeMessages(data.results);

                    this.messages =
                        mergeMessageLists(
                            apiMessages,
                            this.messages
                        );

                    this.messageListResultsDto =
                        Object.assign({}, data, {
                            results: apiMessages
                        });

                    this.msgNextPageLoader =
                        data.next || null;

                    this.lastLiveMsgData = null;
                    this.saveActiveRoomToCache();
                })
                .catch((error) => {
                    console.error(
                        'Unable to refresh chat messages:',
                        error
                    );
                })
                .then(() => {
                    if (
                        requestId ===
                        this.currentChatRequestId
                    ) {
                        this.isRefreshingMessages =
                            false;
                    }
                });
        }
    }
};
